/**
 * A voice note dictated live through the browser's speech service (ADR-002 Part III §24.5), shared by the in-app
 * Capture screen and the personal capture link page (/c/:link) so the two cannot drift.
 *
 * It drives `lib/speech/session.ts` (`startLive`): words appear in the note as the member speaks, final words are
 * appended at the END of the text (so earlier edits are never overwritten), and the phrase still being heard is
 * `interim`. One note gets at most CAPTURE_LIVE_MAX_SECONDS (2:00) of speech, counted across "Record more", which is
 * also the contract's `audio_seconds` limit. No audio is recorded, kept or sent by this app (I5): the browser's
 * vendor hears it, and only the text the member checked goes to POST /capture/notes.
 *
 * Firefox has no recogniser (`probe()` unsupported): `voice` is false and the member types the note.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { CAPTURE_LIVE_MAX_SECONDS } from '@amplispot/contracts';
import { probe } from '../../lib/speech/probe';
import { useMicLevel } from '../../lib/speech/level';
import { appendWords, startLive, type LiveSession, type StopReason } from '../../lib/speech/session';

export const CAP_S = CAPTURE_LIVE_MAX_SECONDS;
/** The too-short error Capture and the link page share; while it shows, the count turns red (capture-v2 D3). */
export const TOO_SHORT = 'Say or type a little more';
const CAP_MS = CAP_S * 1000;

export type RecState = 'idle' | 'starting' | 'live' | 'stopped';

const onlineNow = () => navigator.onLine;
const onlineSub = (cb: () => void) => {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
};

/** Is the browser online? The speech service needs the internet (ADR-002 §31: Record more waits for it). */
export function useOnline(): boolean {
  return useSyncExternalStore(onlineSub, onlineNow, () => true);
}

export function useVoiceRecorder(opts: {
  /** Recognised words, final: append them to the note (the caller uses `appendWords`). */
  onFinal: (text: string) => void;
}) {
  const voice = useState(() => probe() === 'supported')[0];
  const online = useOnline();
  const [rec, setRec] = useState<RecState>('idle');
  const [usedMs, setUsedMs] = useState(0);
  const [segMs, setSegMs] = useState(0);
  const [interim, setInterim] = useState('');
  const [why, setWhy] = useState<StopReason | null>(null);
  const session = useRef<LiveSession | null>(null);
  // Did speech put any words in the note? Only then is it sent as dictated live.
  const heard = useRef(false);
  const o = useRef(opts);
  o.current = opts;

  const recording = rec === 'live' || rec === 'starting';
  const level = useMicLevel(rec === 'live');
  const totalS = Math.min(CAP_S, Math.floor((usedMs + segMs) / 1000));
  const leftS = CAP_S - totalS;
  const usedS = Math.min(CAP_S, Math.round(usedMs / 1000));

  // The recording belongs to the screen (ADR-002 §24.3): leaving it stops listening; nothing runs in the background.
  useEffect(() => () => session.current?.stop(), []);

  const record = () => {
    if (!voice || session.current) return;
    const budget = CAP_MS - usedMs;
    if (budget < 1000) return;
    setWhy(null);
    setSegMs(0);
    setRec('starting');
    try {
      session.current = startLive({
        budgetMs: budget,
        onStart: () => setRec('live'),
        onFinal: (t) => {
          if (t.trim()) heard.current = true;
          o.current.onFinal(t);
        },
        onInterim: setInterim,
        onTick: setSegMs,
        onEnd: (reason, used) => {
          session.current = null;
          setUsedMs((u) => Math.min(CAP_MS, u + used));
          setSegMs(0);
          setInterim('');
          setRec('stopped');
          setWhy(reason);
        },
      });
    } catch {
      session.current = null;
      setRec('stopped');
      setWhy('error');
    }
  };

  const stop = () => session.current?.stop();

  /** Start the note again: the 2:00 budget is back (the caller clears the text). */
  const reset = () => {
    session.current?.stop();
    setRec('idle');
    setUsedMs(0);
    setSegMs(0);
    setInterim('');
    setWhy(null);
    heard.current = false;
  };

  /**
   * How the text was made, for POST /capture/notes: dictated live when speech put any words in it (the member may
   * have edited or typed more since: the text is what they checked), with how long they spoke; otherwise typed.
   */
  const outgoing = (): { live?: { seconds: number } } => (heard.current ? { live: { seconds: usedS } } : {});

  return { voice, online, rec, recording, why, interim, level, totalS, leftS, usedS, record, stop, reset, outgoing };
}

export type VoiceRecorder = ReturnType<typeof useVoiceRecorder>;

export { appendWords };
